// shared.jsx — data, hooks, primitives shared across the panel.
// Ported from design/new design/shared.jsx. Only changes vs. design source:
//  - ESM imports/exports replace window globals + in-browser Babel
//  - room.photo + room.detail paths get the asset base URL prefixed
//    so they resolve under the PWA origin root.

import * as React from 'react';
import { dragEngaged, relativeLevel } from './music/volume-drag.js';

// Static asset (room photo) base path. Cloudflare serves
// panel/dist-pwa/ at /, and Vite dev serves panel/public/ at / —
// both flat at the origin. ASSET_VERSION query string forces refetch
// when a photo is replaced.
const ASSET_BASE = '/';
const ASSET_VERSION = '2';
const asset = (p) => p ? `${ASSET_BASE}${p}?v=${ASSET_VERSION}` : p;

// Room model reconciled against ../ha-av/HANDOFF.md on 2026-05-12.
// See design/HANDOFF.md §"Rooms & entity model" for the rationale.
//
// Six full rooms; standalone Sonos units (kids' rooms, Guest, Laundry,
// portables, Office desk speaker + turntable) live on a separate
// "Speakers" surface that isn't part of ROOMS — they're a flat list of
// media_player entities rendered as a single drawer page.
const ROOMS = [
  {
    id: 'office',
    name: 'Office',
    photoDay: asset('photos/office-day.jpg'),
    photoNight: null,
    photoFocus: 'center 45%',
    temp: 21.0, target: 21, mode: 'cool',
    humidity: 42,
    lights: [
      { id: 'lamp', name: 'Office lamp', on: true, brightness: 80 },
      { id: 'downlights', name: 'Downlights', on: true, brightness: 50 },
      { id: 'strip', name: 'Desk strip', on: false, brightness: 0 },
    ],
    // Office has blackouts + sheers (no louvres).
    blinds: 50, sheer: 60,
    // Sun-tracking day toggle seed (design/mock parity; live value
    // overlays from input_boolean.office_blinds_sun_tracking).
    sunTracking: 'on',
    tv: { on: false, source: '—', volume: 0 },
    sonos: { playing: true, track: 'Brian Eno — An Ending', artist: 'Apollo', volume: 14, room: 'Office' },
    // Live in HA (MAPPINGS.office.scenes); these labels serve the mock only.
    scenes: ['Work', 'Call', 'Relax', 'Done'],
  },
  {
    // Open-plan zone: Kitchen + Living + Dining are physical sub-areas
    // of one AV stack (Sonos Port → DRX-3.1 main + zone 2). Lights
    // below group Kitchen fixtures first, then Dining. Shades are a
    // single average for now; HANDOFF notes a 6+1 split (Kitchen+Living
    // vs Dining) that the Shades modal should eventually surface as
    // two groups.
    id: 'pavilion',
    name: 'Pavilion',
    photoDay: asset('photos/kitchen-day.jpg'),
    photoNight: null,
    photoFocus: 'center 62%',
    temp: 21.5, target: 22, mode: 'auto',
    humidity: 48,
    lights: [
      // Kitchen
      { id: 'kitchen-pendant',    zone: 'kitchen', name: 'Kitchen · Pendant',    on: true,  brightness: 60 },
      { id: 'kitchen-island',     zone: 'kitchen', name: 'Kitchen · Island',     on: true,  brightness: 50 },
      { id: 'kitchen-downlights', zone: 'kitchen', name: 'Kitchen · Downlights', on: false, brightness: 0  },
      { id: 'kitchen-pantry',     zone: 'kitchen', name: 'Kitchen · Pantry',     on: false, brightness: 0  },
      { id: 'kitchen-highlights', zone: 'kitchen', name: 'Kitchen · Highlights', on: false, brightness: 0  },
      // Living
      { id: 'living-pendant',     zone: 'living',  name: 'Living · Pendant',     on: false, brightness: 0  },
      { id: 'living-downlights',  zone: 'living',  name: 'Living · Downlights',  on: true,  brightness: 40 },
      { id: 'living-fire',        zone: 'living',  name: 'Living · Fire',        on: false, brightness: 0  },
      { id: 'living-strip',       zone: 'living',  name: 'Living · Strip',       on: false, brightness: 0  },
      // Dining
      { id: 'dining-chandelier',  zone: 'dining',  name: 'Dining · Chandelier',  on: true,  brightness: 45 },
      { id: 'dining-downlights',  zone: 'dining',  name: 'Dining · Downlights',  on: false, brightness: 0  },
      { id: 'dining-sconces',     zone: 'dining',  name: 'Dining · Sconces',     on: true,  brightness: 30 },
      { id: 'dining-fire',        zone: 'dining',  name: 'Dining · Fire',        on: false, brightness: 0  },
      { id: 'dining-strip',       zone: 'dining',  name: 'Dining · Strip',       on: false, brightness: 0  },
    ],
    blinds: 70,
    // Three Pavilion louvres (high / pool / west) live in MAPPINGS with
    // open/close pulse-switch pairs. Seed initial state 'unknown' for
    // each; the Pool louvre's live cover entity drives its actual
    // state when wired.
    louvres: [
      { id: 'high', name: 'High', state: 'unknown' },
      { id: 'pool', name: 'Pool', state: 'unknown' },
      { id: 'west', name: 'West', state: 'unknown' },
    ],
    tv: { on: false, source: 'Apple TV', volume: 0 },
    sonos: { playing: true, track: 'Hania Rani — Esja', artist: 'Esja', volume: 22, room: 'Pavilion' },
    // Pavilion has a four-way listening mode picker (Silent / Kitchen
    // / Dining / Both) — derived from drx_3_1 + drx_3_1_zone_2 +
    // input_boolean.pavilion_extend_dining. Mock value here; live
    // overlay TBD.
    pavilionMode: 'kitchen',
    scenes: ['Morning', 'Cooking', 'Dinner', 'Wine & Cheese', 'Clean Up'],
  },
  {
    id: 'rumpus',
    name: 'Rumpus',
    photoDay: asset('photos/rumpus-day.jpg'),
    photoNight: null,
    photoFocus: 'center 55%',
    temp: 22.1, target: 22, mode: 'heat',
    humidity: 44,
    lights: [
      { id: 'front-downlights', name: 'Front downlights', on: false, brightness: 0  },
      { id: 'rear-downlights',  name: 'Rear downlights',  on: false, brightness: 0  },
      { id: 'hall-downlights',  name: 'Hall downlights',  on: false, brightness: 0  },
      { id: 'rear-strip',       name: 'Rear LED strip',   on: false, brightness: 0  },
      { id: 'stair-strip',      name: 'Stair LED strip',  on: true,  brightness: 20 },
    ],
    // Single Rumpus louvre on a Fibaro open/close pulse-pair.
    louvres: [
      { id: 'rumpus', name: 'Louvre', state: 'unknown' },
    ],
    tv: { on: true, source: 'Apple TV', volume: 18, app: 'Severance · S2 E4' },
    sonos: { playing: false, track: 'Bon Iver — Holocene', artist: 'For Emma', volume: 0, room: 'Rumpus' },
    scenes: ['Movie', 'Reading', 'Game Night', 'Goodnight'],
  },
  {
    id: 'master_bed',
    name: 'Master Bedroom',
    photoDay: asset('photos/master-day.jpg'),
    photoNight: null,
    photoFocus: 'center 62%',
    temp: 20.4, target: 20, mode: 'auto',
    humidity: 50,
    lights: [
      { id: 'downlights',     zone: 'bedroom',  name: 'Bedroom · Downlights', on: true,  brightness: 30 },
      { id: 'bedside',        zone: 'bedroom',  name: 'Bedroom · Bedside',    on: false, brightness: null },  // switch.* — on/off only
      { id: 'wardrobe',       zone: 'wardrobe', name: 'Wardrobe · Downlights',on: false, brightness: 0  },
      { id: 'wardrobe-strip', zone: 'wardrobe', name: 'Wardrobe · Strip',     on: false, brightness: 0  },
    ],
    // Master Bedroom has no blinds and no louvre (user confirmed
    // 2026-05-24). Both fields previously carried seed-only mocks
    // with no HA mapping — removed so the Shades tile doesn't render.
    sonos: { playing: false, track: 'Ólafur Arnalds — Near Light', artist: 'Living Room Songs', volume: 0, room: 'Master Bedroom' },
    scenes: ['Wake', 'Reading', 'Wind Down', 'Goodnight'],
  },
  {
    // Audio + shades only. No climate, no TV. Lights present but
    // fixture inventory TBD against the running HA.
    id: 'master_ensuite',
    name: 'Master Ensuite',
    photoDay: asset('photos/ensuite-day.jpg'),
    photoNight: null,
    photoFocus: 'center 72%',
    lights: [
      { id: 'downlights', name: 'Downlights', on: false, brightness: 0 },
      { id: 'strip',      name: 'LED strip',  on: false, brightness: 0 },
      { id: 'vanity-strip', name: 'Under-vanity strip', on: false, brightness: 0 },
    ],
    blinds: 40,
    sonos: { playing: false, track: 'Nils Frahm — Says', artist: 'Spaces', volume: 0, room: 'Master Ensuite' },
    scenes: ['Morning', 'Bath', 'Wind Down', 'Off'],
  },
  {
    id: 'pool',
    name: 'Pool',
    photoDay: asset('photos/pool-day.jpg'),
    photoNight: null,
    photoFocus: 'center 50%',
    // outdoor — no HVAC, no screen, no shades. Sonos-only; TOSLINK
    // straight into the SR2-125. No HA-side amp coordination.
    poolTemp: 26.4,
    lights: [
      { id: 'pool', name: 'Pool lights', on: false, brightness: 0 },
      { id: 'wall', name: 'Wall lights', on: false, brightness: 0 },
    ],
    sonos: { playing: false, track: 'Khruangbin — Maria También', artist: 'Con Todo el Mundo', volume: 0, room: 'Pool' },
    scenes: ['Swim', 'Sunset', 'Entertain', 'Off'],
  },
];

// ────────────────────────────────────────────────────────────────────────────
// Hooks
// ────────────────────────────────────────────────────────────────────────────

const { useState, useEffect, useMemo, useRef, useCallback } = React;

// Deep-clone the seed so each direction owns its own state.
// Note: the production panel uses usePanelRooms (in hooks/use-panel-rooms.jsx)
// to overlay live HA state. useRoomState is kept here for dev/standalone
// preview and as the contract reference.
function useRoomState(initialId = 'rumpus') {
  const [rooms, setRooms] = useState(() => JSON.parse(JSON.stringify(ROOMS)));
  const [activeId, setActiveId] = useState(initialId);
  const active = rooms.find(r => r.id === activeId) || rooms[0];

  const update = useCallback((id, patch) => {
    setRooms(prev => prev.map(r => r.id === id ? { ...r, ...patch } : r));
  }, []);

  const updateLight = useCallback((roomId, lightId, patch) => {
    setRooms(prev => prev.map(r => {
      if (r.id !== roomId) return r;
      return {
        ...r,
        lights: r.lights.map(l => l.id === lightId ? { ...l, ...patch } : l),
      };
    }));
  }, []);

  return { rooms, active, activeId, setActiveId, update, updateLight };
}

// Viewport size classifier: 'phone' | 'tablet' | 'laptop'
function useViewport() {
  const get = () => {
    const w = window.innerWidth;
    if (w < 640) return 'phone';
    if (w < 1100) return 'tablet';
    return 'laptop';
  };
  const [vp, setVp] = useState(get);
  useEffect(() => {
    const onR = () => setVp(get());
    window.addEventListener('resize', onR);
    return () => window.removeEventListener('resize', onR);
  }, []);
  return vp;
}

// Comfort-mode toggle — bumps sans body text ~15% panel-wide for users
// who find the default Atelier hierarchy too small. Persists across
// sessions via localStorage so the choice survives reload + app
// re-mount; defaults off.
function useComfort() {
  const KEY = 'ha-controller:comfort';
  const [on, setOn] = useState(() => {
    try { return localStorage.getItem(KEY) === '1'; } catch (e) { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(KEY, on ? '1' : '0'); } catch (e) { /* private mode */ }
  }, [on]);
  return [on, setOn];
}

// Extract a Spotify playlist ID from a pasted share URL, URI, or bare
// ID. Accepts open.spotify.com/playlist/<id>?si=..., spotify:playlist:<id>,
// and a bare <id>. Returns null if nothing playlist-shaped is found.
function parseSpotifyPlaylistId(raw) {
  if (!raw) return null;
  const s = String(raw).trim();
  const m = s.match(/playlist[/:]([A-Za-z0-9]+)/);
  if (m) return m[1];
  if (/^[A-Za-z0-9]+$/.test(s)) return s;
  return null;
}

// Pinned Spotify playlists. Lets the user surface playlists the Web API
// won't return from /me/playlists — notably "Made For You" algorithmic
// mixes (Daily Mix, "<name> Mix"), which Spotify omits from the library
// list even when saved (confirmed: paged all 80 playlists, mix absent).
// The user pastes a share link; the caller resolves it once via
// /v1/playlists/{id} and stores the resolved tile here, so rendering the
// Library costs zero extra Spotify calls (rate-limit hygiene). Persisted
// in localStorage, keyed per-device.
function usePinnedPlaylists() {
  const KEY = 'ha-controller:pinned-playlists';
  const [pins, setPins] = useState(() => {
    try {
      const v = JSON.parse(localStorage.getItem(KEY) || '[]');
      return Array.isArray(v) ? v : [];
    } catch (e) { return []; }
  });
  useEffect(() => {
    try { localStorage.setItem(KEY, JSON.stringify(pins)); } catch (e) { /* private mode */ }
  }, [pins]);
  const addPin = useCallback((tile) => {
    if (!tile?.id) return;
    setPins((cur) => cur.some((p) => p.id === tile.id) ? cur : [...cur, tile]);
  }, []);
  const removePin = useCallback((id) => {
    setPins((cur) => cur.filter((p) => p.id !== id));
  }, []);
  return { pins, addPin, removePin };
}

// Auto theme switch by hour: dark before 6, after 18.
// In the panel-deployed version, main.jsx prefers hass.themes.darkMode
// when available and falls back to this for standalone dev preview.
function useAutoTheme() {
  const [dark, setDark] = useState(() => {
    const h = new Date().getHours();
    return h < 6 || h >= 18;
  });
  useEffect(() => {
    const t = setInterval(() => {
      const h = new Date().getHours();
      setDark(h < 6 || h >= 18);
    }, 60000);
    return () => clearInterval(t);
  }, []);
  return [dark, setDark];
}

// Format clock that ticks each minute (cheap)
// The dev harness can shift the panel's clock (`?at=` sets
// `__PANEL_CLOCK_OFFSET_MS__`) so time-of-day views, such as the evening
// weather summary and the greeting, can be screenshotted at any hour.
// Never set in production, where the offset is 0.
const clockNow = () => new Date(Date.now() + (globalThis.__PANEL_CLOCK_OFFSET_MS__ || 0));

function useClock() {
  const [now, setNow] = useState(clockNow);
  useEffect(() => {
    const t = setInterval(() => setNow(clockNow()), 30000);
    return () => clearInterval(t);
  }, []);
  return now;
}

// ────────────────────────────────────────────────────────────────────────────
// Icons — line-drawn, 24px viewBox
// ────────────────────────────────────────────────────────────────────────────

// Deterministic gradient cover-art from a seed string.
function hash(s) { let h = 5381; for (let i = 0; i < s.length; i++) h = ((h << 5) + h) ^ s.charCodeAt(i); return Math.abs(h); }
const ALBUM_PALETTES = [
  ['#3A4A5A', '#A89878', '#1c1a17'],  // misty
  ['#6B5B47', '#D9B07A', '#2A2118'],  // amber
  ['#4A4A52', '#8B7B68', '#1A1A20'],  // dusk
  ['#5E4A3A', '#C49872', '#241612'],  // copper
  ['#3A4A40', '#9CB098', '#1A2018'],  // moss
  ['#52423A', '#A88770', '#221814'],  // clay
];
function CoverArt({ seed = 'untitled', size = 48, radius = 8, label = true, style }) {
  const h = hash(seed);
  const pal = ALBUM_PALETTES[h % ALBUM_PALETTES.length];
  const rot = (h % 360);
  const initials = (seed.match(/[A-Za-zÀ-ÿ0-9]/g) || []).slice(0, 2).join('').toUpperCase();
  return (
    <div style={{
      width: size, height: size, borderRadius: radius, overflow: 'hidden',
      position: 'relative', flexShrink: 0,
      background: `linear-gradient(${rot}deg, ${pal[0]} 0%, ${pal[1]} 55%, ${pal[2]} 100%)`,
      boxShadow: '0 6px 20px rgba(0,0,0,.22), inset 0 0 0 1px rgba(255,255,255,.08)',
      ...style,
    }}>
      <div style={{
        position: 'absolute', inset: 0,
        background: `radial-gradient(circle at ${20 + (h % 60)}% ${20 + ((h>>3) % 60)}%, rgba(255,255,255,.18), transparent 60%)`,
      }}/>
      {label && (
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: '"Cormorant Garamond", serif', fontStyle: 'italic',
          fontSize: size * 0.34, color: 'rgba(255,255,255,.82)', letterSpacing: '.02em',
          textShadow: '0 1px 4px rgba(0,0,0,.25)',
        }}>{initials}</div>
      )}
    </div>
  );
}
// Brand-mark placeholders for the Screen / Sonos tiles when nothing's
// playing. SVGs use preserveAspectRatio=meet so the wordmark stays
// centred and letterboxed inside whatever square the caller provides.
const APPLE_TV_PATHS = [
  'M102.4,85.3c0.2,21.4,18.8,28.6,19,28.7c-0.2,0.5-3,10.2-9.8,20.1c-5.9,8.6-12,17.2-21.7,17.4c-9.5,0.2-12.5-5.6-23.4-5.6c-10.8,0-14.2,5.4-23.2,5.8C34.1,152,27,142.3,21,133.7C8.9,116.1-0.4,84,12.1,62.4c6.2-10.8,17.3-17.6,29.3-17.7c9.1-0.2,17.8,6.2,23.4,6.2c5.6,0,16.1-7.6,27.1-6.5c4.6,0.2,17.6,1.9,25.9,14C117.1,58.8,102.3,67.4,102.4,85.3 M84.6,32.7c4.9-6,8.3-14.3,7.4-22.6c-7.1,0.3-15.7,4.7-20.9,10.7c-4.6,5.3-8.6,13.8-7.5,21.9C71.5,43.3,79.7,38.6,84.6,32.7',
  'M190.8,150.1c-6.1,1.7-12.4,2.5-18.8,2.5c-14.9,0-22.3-8.4-22.3-25.3V52.8h-12.9V39.3h13.6l0-17.6l17.9-7.3v25H189v13.5h-20.6v70.4c0,5.4,0.9,9.1,2.6,11.3s4.8,3.2,9.1,3.2c2.5,0,6-0.5,10.7-1.4V150.1z',
  'M255.6,150.9h-22L193.1,39.3h19.8l24.5,72.6c0.9,2.8,3.3,10.9,7.1,24.4l3.6-12.1l4-12.2l25.4-72.8h19.7L255.6,150.9z',
];

function AppleTvMark({ radius = 6, style }) {
  return (
    <div style={{
      width: '100%', height: '100%', borderRadius: radius, flexShrink: 0,
      background: '#000', overflow: 'hidden', display: 'flex',
      alignItems: 'center', justifyContent: 'center', padding: '14%',
      boxSizing: 'border-box',
      boxShadow: '0 8px 20px rgba(0,0,0,.32), inset 0 0 0 1px rgba(255,255,255,.06)',
      ...style,
    }}>
      <svg viewBox="0 0 300 160" width="100%" height="100%"
           preserveAspectRatio="xMidYMid meet" fill="#fff">
        {APPLE_TV_PATHS.map((d, i) => <path key={i} d={d}/>)}
      </svg>
    </div>
  );
}

const SONOS_TRANSFORM = 'translate(-100.56 -99.64)';
function SonosMark({ radius = 6, style }) {
  return (
    <div style={{
      width: '100%', height: '100%', borderRadius: radius, flexShrink: 0,
      background: '#000', overflow: 'hidden', display: 'flex',
      alignItems: 'center', justifyContent: 'center', padding: '18% 12%',
      boxSizing: 'border-box',
      boxShadow: '0 8px 20px rgba(0,0,0,.32), inset 0 0 0 1px rgba(255,255,255,.06)',
      ...style,
    }}>
      <svg viewBox="0 0 509.92 100.56" width="100%" height="100%"
           preserveAspectRatio="xMidYMid meet" fill="#fff">
        <polygon points="276.21 57.21 215.82 0.91 215.82 96.36 233.72 96.36 233.72 43.06 294.1 99.27 294.1 3.58 276.21 3.58 276.21 57.21"/>
        <path transform={SONOS_TRANSFORM} d="M238,99.64A50.1,50.1,0,1,0,288,149.74,50.15,50.15,0,0,0,238,99.64Zm0,82.18a32.09,32.09,0,1,1,31.76-32.08A32,32,0,0,1,238,181.82Z"/>
        <path transform={SONOS_TRANSFORM} d="M157.39,150.57c-4.76-3.63-11-6.56-20.33-9.52-18.14-5.76-18.14-10.34-18.14-12.53,0-5.16,5.75-10.5,15.39-10.5a29.11,29.11,0,0,1,16.75,5.79l1.25.94L166.73,115l-1.57-1.87a42.19,42.19,0,0,0-30.85-13.47c-9.28,0-17.81,3-24,8.35a27.34,27.34,0,0,0-9.73,20.51c0,8.22,3.59,15.13,10.67,20.53,4.76,3.63,11,6.56,20.33,9.52,18.14,5.76,18.14,10.34,18.14,12.53,0,5.16-5.76,10.5-15.39,10.5a29,29,0,0,1-16.75-5.79l-1.25-.94-14.43,9.75,1.57,1.87A42.29,42.29,0,0,0,134.31,200c9.27,0,17.8-3,24-8.35a27.36,27.36,0,0,0,9.74-20.51C168.06,162.88,164.47,156,157.39,150.57Z"/>
        <path transform={SONOS_TRANSFORM} d="M473.08,100a50.1,50.1,0,1,0,50.1,50.09A50.15,50.15,0,0,0,473.08,100Zm0,82.18a32.09,32.09,0,1,1,31.76-32.09A32,32,0,0,1,473.08,182.19Z"/>
        <path transform={SONOS_TRANSFORM} d="M553.65,149.27c4.75,3.63,11,6.56,20.33,9.52,18.13,5.76,18.13,10.34,18.13,12.53,0,5.16-5.75,10.5-15.38,10.5A29.08,29.08,0,0,1,560,176l-1.26-.93-14.42,9.74,1.57,1.87a42.22,42.22,0,0,0,30.86,13.47c9.27,0,17.8-3,24-8.35a27.35,27.35,0,0,0,9.74-20.51c0-8.22-3.59-15.13-10.68-20.53-4.75-3.63-11-6.56-20.33-9.52-18.13-5.76-18.13-10.34-18.13-12.53,0-5.16,5.75-10.5,15.38-10.5A29,29,0,0,1,593.47,124l1.26.94,14.42-9.75-1.57-1.87a42.19,42.19,0,0,0-30.86-13.47c-9.27,0-17.8,3-24,8.35A27.35,27.35,0,0,0,543,128.74C543,137,546.57,143.87,553.65,149.27Z"/>
      </svg>
    </div>
  );
}

// TV/streaming posters — vertical, slightly desaturated
function PosterArt({ seed = 'tv', w = 60, h = 86, radius = 6, label, style }) {
  const hh = hash(seed);
  const pal = ALBUM_PALETTES[(hh + 3) % ALBUM_PALETTES.length];
  return (
    <div style={{
      width: w, height: h, borderRadius: radius, overflow: 'hidden',
      position: 'relative', flexShrink: 0,
      background: `linear-gradient(160deg, ${pal[2]} 0%, ${pal[0]} 60%, ${pal[1]} 100%)`,
      boxShadow: '0 8px 20px rgba(0,0,0,.32), inset 0 0 0 1px rgba(255,255,255,.06)',
      ...style,
    }}>
      <div style={{
        position: 'absolute', inset: 0,
        background: `radial-gradient(ellipse at 50% 80%, rgba(255,255,255,.12), transparent 60%)`,
      }}/>
      {label && (
        <div style={{
          position: 'absolute', left: 6, right: 6, bottom: 6,
          fontFamily: '"Cormorant Garamond", serif', fontStyle: 'italic',
          fontSize: w * 0.16, color: 'rgba(255,255,255,.92)', lineHeight: 1.1,
          textShadow: '0 1px 4px rgba(0,0,0,.45)',
        }}>{label}</div>
      )}
    </div>
  );
}

const Icon = ({ d, size = 18, sw = 1.4, fill = 'none', style }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={fill} stroke="currentColor"
       strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" style={style}>
    {d}
  </svg>
);

const I = {
  thermo: <Icon d={<><path d="M14 14.76V4a2 2 0 0 0-4 0v10.76a4 4 0 1 0 4 0Z"/></>}/>,
  // Climate-mode set (Atelier, designed 2026-05-25). Use via the
  // CLIMATE_ICON map below so callsites don't need per-mode ladder
  // switching. `thermo` stays as the documented fallback for any
  // mode not in the map (dry / fan_only / etc.).
  flame: <Icon d={<><path d="M12 21C7.6 21 5 18.2 5 14.6C5 12.4 6.2 11.4 7.2 12C6.6 9.6 8.4 7.4 9.5 6.6C9.2 8.2 10 9.6 11 9.6C12 9.6 12.4 7.4 12 4.8C14 6.4 16 8.4 17.4 11.6C18.6 14.4 18 17.4 16.2 19.2C15 20.4 13.6 21 12 21Z"/><path d="M12 18C10.2 18 9.2 16.6 9.6 15C10 16.2 11 16.6 12 16.4C13 16.2 13.6 15.2 13.6 14.2C14.4 15.4 14.2 17 13.2 17.7C12.8 17.9 12.4 18 12 18Z"/></>}/>,
  snow: <Icon d={<><path d="M12 3V21"/><path d="M4.2 7.5L19.8 16.5"/><path d="M19.8 7.5L4.2 16.5"/><path d="M14 12L13 13.73L11 13.73L10 12L11 10.27L13 10.27Z"/></>}/>,
  auto: <Icon d={<><path d="M11 4H14"/><path d="M13 4L4.5 20"/><path d="M13 4L18.5 20"/><path d="M8.4 13.8H16.2"/><path d="M3 20H6.4"/><path d="M18.5 20Q20.6 21 21.6 18.8"/></>}/>,
  off: <Icon d={<><circle cx="12" cy="12" r="8"/><path d="M8.5 12H15.5"/></>}/>,
  bulb: <Icon d={<><path d="M9 18h6"/><path d="M10 21h4"/><path d="M12 3a6 6 0 0 0-4 10.5c.7.7 1 1.5 1 2.5h6c0-1 .3-1.8 1-2.5A6 6 0 0 0 12 3Z"/></>}/>,
  // Filled-bulb variant for the ON state — same outline, fill applied
  // to the bulb body so the glyph reads "lit" at a glance. ALightsModal
  // swaps between bulb (off, outlined) and bulbOn (on, filled).
  bulbOn: <Icon d={<><path d="M9 18h6"/><path d="M10 21h4"/><path d="M12 3a6 6 0 0 0-4 10.5c.7.7 1 1.5 1 2.5h6c0-1 .3-1.8 1-2.5A6 6 0 0 0 12 3Z" fill="currentColor"/></>}/>,
  blinds: <Icon d={<><path d="M3 4h18"/><path d="M5 4v14"/><path d="M19 4v14"/><path d="M5 8h14"/><path d="M5 12h14"/><path d="M5 16h14"/><path d="M9 20l3 2 3-2"/></>}/>,
  louvre: <Icon d={<><rect x="4" y="4" width="16" height="16" rx="1"/><path d="M7 8l10-1.5"/><path d="M7 12l10-1.5"/><path d="M7 16l10-1.5"/></>}/>,
  tv: <Icon d={<><rect x="2" y="5" width="20" height="13" rx="2"/><path d="M8 21h8"/><path d="M12 18v3"/></>}/>,
  apple: <Icon d={<><path d="M16 3c.2 1.6-.8 3.2-2.6 3.2-.2-1.5.9-3.2 2.6-3.2Z"/><path d="M19 17.3c-.5 1-.7 1.5-1.4 2.3-.9 1.1-2.3 2.4-4 2.4-1.5 0-1.9-1-4-1-2 0-2.5 1-4 1-1.7 0-3-1.2-3.9-2.3C-.4 16.4.7 11 3 8.5c1.1-1.2 2.5-2 3.7-2 1.4 0 2.3 1 3.4 1 1.1 0 1.8-1 3.4-1 1.2 0 2.6.7 3.6 1.9-3.2 1.7-2.7 6.3 1.9 7.9Z"/></>}/>,
  speaker: <Icon d={<><rect x="6" y="3" width="12" height="18" rx="2"/><circle cx="12" cy="14" r="3"/><circle cx="12" cy="7" r="1"/></>}/>,
  play: <Icon d={<><path d="M8 5v14l11-7-11-7Z" fill="currentColor" stroke="none"/></>}/>,
  pause: <Icon d={<><rect x="7" y="5" width="3.5" height="14" fill="currentColor" stroke="none"/><rect x="13.5" y="5" width="3.5" height="14" fill="currentColor" stroke="none"/></>}/>,
  next: <Icon d={<><path d="M5 5l9 7-9 7Z" fill="currentColor" stroke="none"/><rect x="15" y="5" width="2" height="14" fill="currentColor" stroke="none"/></>}/>,
  prev: <Icon d={<><path d="M19 5l-9 7 9 7Z" fill="currentColor" stroke="none"/><rect x="7" y="5" width="2" height="14" fill="currentColor" stroke="none"/></>}/>,
  power: <Icon d={<><path d="M12 3v9"/><path d="M5.6 7.6a8 8 0 1 0 12.8 0"/></>}/>,
  sun: <Icon d={<><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M2 12h2M20 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4"/></>}/>,
  moon: <Icon d={<><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z"/></>}/>,
  cloud: <Icon d={<><path d="M7 18a5 5 0 1 1 1-9.9A6 6 0 0 1 19 11.5 4 4 0 0 1 18 19H7Z"/></>}/>,
  wind: <Icon d={<><path d="M3 8h11a3 3 0 1 0-3-3"/><path d="M3 12h17a3 3 0 1 1-3 3"/><path d="M3 16h7"/></>}/>,
  drop: <Icon d={<><path d="M12 3s6 7 6 11a6 6 0 1 1-12 0c0-4 6-11 6-11Z"/></>}/>,
  arrow: <Icon d={<><path d="M5 12h14"/><path d="M13 6l6 6-6 6"/></>}/>,
  plus: <Icon d={<><path d="M12 5v14M5 12h14"/></>}/>,
  minus: <Icon d={<><path d="M5 12h14"/></>}/>,
  cog: <Icon d={<><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3 1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8 1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z"/></>}/>,
  search: <Icon d={<><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></>}/>,
  home: <Icon d={<><path d="M3 11l9-8 9 8"/><path d="M5 10v10h14V10"/></>}/>,
  shuffle: <Icon d={<><path d="M16 3h5v5"/><path d="M4 20l17-17"/><path d="M21 16v5h-5"/><path d="M15 15l6 6"/><path d="M4 4l5 5"/></>}/>,
  list: <Icon d={<><path d="M8 6h13M8 12h13M8 18h13"/><circle cx="4" cy="6" r="1" fill="currentColor"/><circle cx="4" cy="12" r="1" fill="currentColor"/><circle cx="4" cy="18" r="1" fill="currentColor"/></>}/>,
  // Utility-row glyphs (Master Ensuite + Master Bed Utilities card).
  // Three curved blades around a hub — reads as a fan at small sizes.
  fan: <Icon d={<><circle cx="12" cy="12" r="1.5"/><path d="M12 10.5C12 7 9 4 6 5C5 6 5 8 6 9C8 10.5 10 10.5 12 10.5Z"/><path d="M13.5 12C17 12 20 9 19 6C18 5 16 5 15 6C13.5 8 13.5 10 13.5 12Z"/><path d="M12 13.5C12 17 15 20 18 19C19 18 19 16 18 15C16 13.5 14 13.5 12 13.5Z"/><path d="M10.5 12C7 12 4 15 5 18C6 19 8 19 9 18C10.5 16 10.5 14 10.5 12Z"/></>}/>,
  // Stacked horizontal bars — towel-rail / radiator silhouette.
  radiator: <Icon d={<><rect x="4" y="6" width="16" height="2" rx="1"/><rect x="4" y="11" width="16" height="2" rx="1"/><rect x="4" y="16" width="16" height="2" rx="1"/><path d="M6 4v2M10 4v2M14 4v2M18 4v2"/></>}/>,
  // Music-card content-type glyphs (Atelier, designed 2026-05-28).
  // Used as eyebrow icons in S1 search sections and S2 detail headers
  // via TYPE_ICON below. Same 24×24 viewBox + 1.4 stroke as the rest.
  track: <Icon d={<><ellipse cx="8" cy="17" rx="3" ry="2.2" fill="currentColor" stroke="none"/><path d="M11 17V5"/><path d="M11 5c2.2 1 5 2.2 5 6"/></>}/>,
  album: <Icon d={<><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2.5"/><circle cx="12" cy="12" r="0.7" fill="currentColor" stroke="none"/></>}/>,
  playlist: <Icon d={<><path d="M4 6h16M4 11h16M4 16h9"/><path d="M16 14l5 3-5 3z" fill="currentColor" stroke="none"/></>}/>,
  artist: <Icon d={<><rect x="9" y="3" width="6" height="11" rx="3"/><path d="M6 12a6 6 0 0 0 12 0"/><path d="M12 18v3"/><path d="M9.5 21h5"/></>}/>,
  podcast: <Icon d={<><circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none"/><path d="M9.2 14.8a4 4 0 0 1 0-5.6"/><path d="M14.8 9.2a4 4 0 0 1 0 5.6"/><path d="M6.5 17.5a8 8 0 0 1 0-11"/><path d="M17.5 6.5a8 8 0 0 1 0 11"/></>}/>,
  episode: <Icon d={<><circle cx="12" cy="12" r="9"/><path d="M10 8.5v7l6-3.5-6-3.5z" fill="currentColor" stroke="none"/></>}/>,
};

// Climate-mode → glyph map. Lets eyebrow + chip-rail callsites follow
// the active mode without per-callsite mode-switching. Any mode not
// listed here (dry / fan_only) falls back via `|| I.thermo` at the
// callsite, so adding HA modes is non-breaking.
// Keyed by HA hvac_mode. `heat_cool` is what every unit here actually
// reports for auto changeover — all six climate entities list
// ["heat_cool","off","fan_only","dry","heat","cool"] and none of them
// offer a mode called `auto`. The `auto` key is kept only because the
// unmapped mock rooms in ROOMS still seed it; live rooms never hit it.
// Anything unmapped (dry / fan_only) falls back to I.thermo at callsites.
const CLIMATE_ICON = { heat: I.flame, cool: I.snow, heat_cool: I.auto, auto: I.auto, off: I.off };

// Display labels for hvac modes — `heat_cool` is correct to send to HA
// but reads as machine output on a wall panel.
const CLIMATE_MODE_LABEL = {
  heat_cool: 'Auto', auto: 'Auto', heat: 'Heat', cool: 'Cool', off: 'Off',
  dry: 'Dry', fan_only: 'Fan',
};

// Music content-type → glyph map. Lets the music card resolve a glyph
// by `kind` (track / album / playlist / artist / show / episode) at
// the eyebrow callsite. `show` aliases to podcast — a show IS a
// podcast series in Spotify's vocabulary.
const TYPE_ICON = {
  track:    I.track,
  album:    I.album,
  playlist: I.playlist,
  artist:   I.artist,
  show:     I.podcast,
  podcast:  I.podcast,
  episode:  I.episode,
};

// ────────────────────────────────────────────────────────────────────────────
// Tiny shared primitives
// ────────────────────────────────────────────────────────────────────────────

// Range slider that fills with currentColor up to its value.
// Optional `onCommit` fires on pointer-up — used by the scrubber to
// defer the expensive seek service call until the drag completes
// (onChange fires continuously while dragging).
// Slider — a 44px-tall target with an 8px track and a 28px thumb. The
// family rejected a 3px slider in May 2026 as too small to hit; where a
// continuous level is genuinely needed (volume, EQ, seek), this is the
// size it has to be.
// `relative` (volume) makes a touch alone do nothing: the level moves only
// once the finger slides, by the distance slid. See volume-drag.js.
const Slider = ({ value, onChange, onCommit, min = 0, max = 100, step = 1, color, track, height = 8, style, relative = false }) => {
  const ref = useRef(null);
  const drag = useRef(false);
  const start = useRef(null);
  const v = Math.max(min, Math.min(max, value));
  const pct = ((v - min) / (max - min)) * 100;

  const setFromClientX = (clientX) => {
    const el = ref.current; if (!el) return;
    const r = el.getBoundingClientRect();
    const p = Math.max(0, Math.min(1, (clientX - r.left) / r.width));
    const raw = min + p * (max - min);
    const stepped = Math.round(raw / step) * step;
    onChange(Math.max(min, Math.min(max, stepped)));
  };

  // A relative touch that never slid sent nothing, so it has nothing to
  // commit either.
  const endDrag = () => {
    const sent = !relative || start.current?.engaged;
    drag.current = false; start.current = null;
    if (sent) onCommit?.();
  };

  return (
    <div ref={ref}
         onPointerDown={(e) => {
           drag.current = true; e.target.setPointerCapture?.(e.pointerId);
           if (relative) start.current = { x: e.clientX, v, engaged: false };
           else setFromClientX(e.clientX);
         }}
         onPointerMove={(e) => {
           if (!drag.current) return;
           if (!relative) { setFromClientX(e.clientX); return; }
           const s0 = start.current; if (!s0) return;
           const dx = e.clientX - s0.x;
           if (!s0.engaged && !dragEngaged(dx)) return;
           s0.engaged = true;
           const width = ref.current?.getBoundingClientRect().width;
           onChange(relativeLevel({ startValue: s0.v, dx, width, min, max, step }));
         }}
         onPointerUp={() => { endDrag(); }}
         onPointerCancel={() => { endDrag(); }}
         style={{
           position: 'relative', height: 44, cursor: 'pointer',
           display: 'flex', alignItems: 'center',
           touchAction: 'none', userSelect: 'none',
           ...style,
         }}>
      <div style={{
        position: 'absolute', left: 0, right: 0, height,
        background: track || 'rgba(128,124,118,.22)', borderRadius: 999,
      }}/>
      <div style={{
        position: 'absolute', left: 0, width: `${pct}%`, height,
        background: color || 'currentColor', borderRadius: 999,
        transition: drag.current ? 'none' : 'width .18s ease',
      }}/>
      <div style={{
        position: 'absolute', left: `calc(${pct}% - 14px)`, width: 28, height: 28,
        background: '#fff', borderRadius: '50%',
        boxShadow: '0 1px 5px rgba(34,33,32,.28)',
        transition: drag.current ? 'none' : 'left .18s ease',
      }}/>
    </div>
  );
};

// Vertical slider variant
const VSlider = ({ value, onChange, min = 0, max = 100, height = 140, color, track }) => {
  const ref = useRef(null);
  const drag = useRef(false);
  const v = Math.max(min, Math.min(max, value));
  const pct = ((v - min) / (max - min)) * 100;
  const setFromClientY = (clientY) => {
    const el = ref.current; if (!el) return;
    const r = el.getBoundingClientRect();
    const p = Math.max(0, Math.min(1, 1 - (clientY - r.top) / r.height));
    onChange(Math.round(min + p * (max - min)));
  };
  return (
    <div ref={ref}
         onPointerDown={(e) => { drag.current = true; e.target.setPointerCapture?.(e.pointerId); setFromClientY(e.clientY); }}
         onPointerMove={(e) => { if (drag.current) setFromClientY(e.clientY); }}
         onPointerUp={() => { drag.current = false; }}
         style={{ position: 'relative', width: 36, height, cursor: 'pointer', touchAction: 'none', userSelect: 'none' }}>
      <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', top: 0, bottom: 0, width: 6, background: track || 'rgba(0,0,0,.08)', borderRadius: 999 }}/>
      <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', bottom: 0, height: `${pct}%`, width: 6, background: color || 'currentColor', borderRadius: 999 }}/>
      <div style={{ position: 'absolute', left: '50%', bottom: `calc(${pct}% - 11px)`, transform: 'translateX(-50%)', width: 22, height: 22, background: '#fff', borderRadius: '50%', boxShadow: '0 1px 3px rgba(0,0,0,.25), 0 0 0 1px rgba(0,0,0,.06)' }}/>
    </div>
  );
};

// Toggle pill
const Toggle = ({ on, onChange, color }) => (
  <button onClick={() => onChange(!on)} aria-pressed={on}
    style={{
      width: 40, height: 22, borderRadius: 999, border: 'none',
      background: on ? (color || 'currentColor') : 'rgba(0,0,0,.14)',
      position: 'relative', cursor: 'pointer', padding: 0,
      transition: 'background .18s',
    }}>
    <span style={{
      position: 'absolute', top: 2, left: on ? 20 : 2,
      width: 18, height: 18, background: '#fff', borderRadius: '50%',
      boxShadow: '0 1px 2px rgba(0,0,0,.2)',
      transition: 'left .18s',
    }}/>
  </button>
);

// Time/clock formatter
const fmtTime = (d) => {
  const h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
};
const fmtDate = (d) => d.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' });

// Greeting based on hour
const greeting = (d) => {
  const h = d.getHours();
  if (h < 5) return 'Good night';
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
};

export {
  ROOMS, ASSET_BASE,
  useRoomState, useClock, useViewport, useAutoTheme, useComfort,
  usePinnedPlaylists, parseSpotifyPlaylistId,
  Icon, I, CLIMATE_ICON, CLIMATE_MODE_LABEL, TYPE_ICON, Slider, VSlider, Toggle, CoverArt, PosterArt, AppleTvMark, SonosMark,
  fmtTime, fmtDate, greeting,
};
